import type {
	CreatePromptInput,
	EvalRun,
	Prompt,
	PromptEvalGate,
	PromptLabel,
	PromptLabelPointer,
	PromptListRecord,
	PromptRenderResult,
	PromptTestCase,
	PromptVersion,
	UpdatePromptInput,
} from '@/domain/prompt.types'
import { apiClient } from './api.client'

/**
 * The list endpoint returns a `{ data, pagination }` envelope; single objects return
 * the DTO directly. A 204 or empty body is tolerated so an empty library renders as
 * empty rather than throwing.
 */
const listOf = <T>(response: { status: number; data?: { data?: T[] } }): T[] =>
	response.status === 204 ? [] : (response.data?.data ?? [])

const arrayOf = <T>(response: { status: number; data?: T[] }): T[] =>
	response.status === 204 || !response.data ? [] : response.data

export const promptsApi = {
	list: (): Promise<Prompt[]> => apiClient.get<{ data: Prompt[] }>('/prompts').then(listOf),

	byId: (id: string): Promise<Prompt> => apiClient.get<Prompt>(`/prompts/${id}`).then((r) => r.data),

	/** By the stable handle — how live code and agents resolve a prompt. */
	byCode: (code: string): Promise<Prompt> => apiClient.get<Prompt>(`/prompts/code/${code}`).then((r) => r.data),

	create: (input: CreatePromptInput): Promise<Prompt> => apiClient.post<Prompt>('/prompts', input).then((r) => r.data),

	update: (id: string, patch: UpdatePromptInput): Promise<Prompt> =>
		apiClient.put<Prompt>(`/prompts/${id}`, patch).then((r) => r.data),

	remove: (id: string): Promise<void> => apiClient.delete(`/prompts/${id}`).then(() => undefined),

	publish: (id: string): Promise<PromptVersion> =>
		apiClient.post<PromptVersion>(`/prompts/${id}/publish`).then((r) => r.data),

	/**
	 * Prompts that apply to a classification tuple.
	 *
	 * The axis filtering happens SERVER-side through the resolution query: the list
	 * response carries no classification, so a client-side facet filter would have
	 * nothing to match on.
	 */
	applicable: (context: Record<string, string[]>, type?: string): Promise<Prompt[]> => {
		const params = new URLSearchParams()
		// `<axisCode>=a,b` — one comma-joined param per axis, which is what
		// `parseResolveQuery` splits. API Gateway collapses repeated params, so a
		// repeated-key encoding would silently lose every value but one.
		for (const [axis, values] of Object.entries(context)) {
			if (values.length > 0) params.set(axis, values.join(','))
		}
		if (type) params.set('type', type)
		// `lenient` so a prompt left open on an axis still matches: an empty axis means
		// "applies to every value", and strict mode would hide exactly those.
		params.set('mode', 'lenient')
		return apiClient.get<Prompt[]>(`/prompts/applicable?${params.toString()}`).then(arrayOf)
	},

	/** What the publish dialog warns on — the last settled eval result and the tenant's gate. */
	evalGate: (id: string): Promise<PromptEvalGate> =>
		apiClient.get<PromptEvalGate>(`/prompts/${id}/eval-gate`).then((r) => r.data),

	versions: (id: string): Promise<PromptVersion[]> =>
		apiClient.get<PromptVersion[]>(`/prompts/${id}/versions`).then(arrayOf),

	labels: (id: string): Promise<PromptLabelPointer[]> =>
		apiClient.get<PromptLabelPointer[]>(`/prompts/${id}/labels`).then(arrayOf),

	/** Promotion and rollback are both a label repoint — no new version is created. */
	moveLabel: (id: string, label: PromptLabel, version: number, pinned?: boolean): Promise<PromptLabelPointer> =>
		apiClient.put<PromptLabelPointer>(`/prompts/${id}/labels/${label}`, { version, pinned }).then((r) => r.data),

	/**
	 * Render a prompt. `?head=true` previews the working draft against live lists;
	 * omitted, the production label's frozen version is used.
	 */
	render: (id: string, values: Record<string, unknown>, head = false): Promise<PromptRenderResult> =>
		apiClient
			.post<PromptRenderResult>(`/prompts/${id}/render${head ? '?head=true' : ''}`, { values })
			.then((r) => r.data),

	classify: (id: string, classification: Record<string, string[]>): Promise<void> =>
		apiClient.post(`/prompts/${id}/classification`, { classification }).then(() => undefined),

	// ─── Test cases and eval runs ─────────────────────────────────────────────

	testCases: (id: string): Promise<PromptTestCase[]> =>
		apiClient.get<PromptTestCase[]>(`/prompts/${id}/test-cases`).then(arrayOf),

	createTestCase: (
		id: string,
		input: Omit<PromptTestCase, 'id' | 'promptId' | 'origin' | 'isActive' | 'createdAt' | 'updatedAt'>,
	): Promise<PromptTestCase> => apiClient.post<PromptTestCase>(`/prompts/${id}/test-cases`, input).then((r) => r.data),

	updateTestCase: (caseId: string, patch: Partial<PromptTestCase>): Promise<PromptTestCase> =>
		apiClient.patch<PromptTestCase>(`/prompt-test-cases/${caseId}`, patch).then((r) => r.data),

	deleteTestCase: (caseId: string): Promise<void> =>
		apiClient.delete(`/prompt-test-cases/${caseId}`).then(() => undefined),

	/** 202 — returns a run id; results arrive asynchronously. */
	requestEvalRun: (id: string): Promise<{ runId: string; totalCases: number }> =>
		apiClient.post<{ runId: string; totalCases: number }>(`/prompts/${id}/evals/run`).then((r) => r.data),

	evalRuns: (id: string): Promise<EvalRun[]> => apiClient.get<EvalRun[]>(`/prompts/${id}/evals/runs`).then(arrayOf),

	evalRun: (runId: string): Promise<EvalRun> =>
		apiClient.get<EvalRun>(`/prompt-eval-runs/${runId}`).then((r) => r.data),

	/** 202 — proposals arrive asynchronously and land flagged as generated. */
	generateTestCases: (id: string): Promise<{ requested: number }> =>
		apiClient.post<{ requested: number }>(`/prompts/${id}/test-cases/generate`).then((r) => r.data),

	generateOutputSchema: (id: string): Promise<{ requested: boolean }> =>
		apiClient.post<{ requested: boolean }>(`/prompts/${id}/output-schema/generate`).then((r) => r.data),

	// ─── Shared lists ─────────────────────────────────────────────────────────

	lists: (): Promise<PromptListRecord[]> => apiClient.get<PromptListRecord[]>('/prompt-lists').then(arrayOf),

	createList: (input: {
		code: string
		name: string
		items: { value: string; label: string }[]
	}): Promise<PromptListRecord> => apiClient.post<PromptListRecord>('/prompt-lists', input).then((r) => r.data),

	updateList: (id: string, patch: Partial<PromptListRecord>): Promise<PromptListRecord> =>
		apiClient.patch<PromptListRecord>(`/prompt-lists/${id}`, patch).then((r) => r.data),

	deleteList: (id: string): Promise<void> => apiClient.delete(`/prompt-lists/${id}`).then(() => undefined),

	/** Shown before a delete, since the backend refuses while a list is referenced. */
	listReferences: (id: string): Promise<{ id: string; code: string }[]> =>
		apiClient.get<{ id: string; code: string }[]>(`/prompt-lists/${id}/references`).then(arrayOf),

	// ─── Tenant defaults ──────────────────────────────────────────────────────

	defaults: (): Promise<{ promptType: string; modelConfig: Record<string, unknown>; isTenantOverride: boolean }[]> =>
		apiClient
			.get<{ promptType: string; modelConfig: Record<string, unknown>; isTenantOverride: boolean }[]>(
				'/prompt-defaults',
			)
			.then(arrayOf),

	updateDefaults: (promptType: string, modelConfig: Record<string, unknown>): Promise<void> =>
		apiClient.put(`/prompt-defaults/${promptType}`, modelConfig).then(() => undefined),
}
